/**
 * A row's name as the drive shows it: the FULL name, with its endings muted
 * (never hidden). One rule for the drive's rows and the pickers' rows, so the
 * same item reads the same in both — a folder `X` and its chain `X.chain` stay
 * distinguishable.
 *
 *   ext  — the SOURCE / identity ending, level-1 mute (.file-ext-muted)
 *   ext2 — the "wrapper" sidecar suffix (`.pmeta`/`.shared`/`.chain`),
 *          level-2 mute (.file-ext-muted-2) — LIGHTER than the source ext,
 *          since it's runtime plumbing (`report.pdf.shared` keeps `.pdf`
 *          more legible than the `.shared`).
 *
 * Wrapper suffixes mute whether the row presents as a file OR a folder (a
 * `.chain` folder pointer / `.shared` folder mount still gets muted).
 * Project/mini-app folders split on their known extension; plain files split
 * on the last dot (dotfiles left alone). Rename edits the full name regardless.
 *
 * Muting is a claim that the suffix is RUNTIME PLUMBING rather than part of
 * the name, so it has to ask the row KIND and not just the name. Downloading a
 * folder with `expandPointers` produces ordinary folders that keep the
 * pointer's name, and re-uploading them makes plain rows called `ddd.shared`
 * -- greying their suffix says "this is a mount" about something that is not
 * one, and cannot become one. Same distinction the icon overlay already draws:
 * `fileType` exists so "a user-renamed foo.shared file doesn't get the Network
 * badge", and the name split simply had not been taught it.
 *
 * Authoritative when present, extension decides when absent -- the same rule
 * `presentsAsContainer` states, so a listing that omits the column keeps the
 * old behaviour for real pointers rather than silently un-muting them.
 */
import { isMiniappFolder, isPmetaFile, isProjectFolder, pointerSuffixIsMeta, stripMiniappFolderExt, stripProjectFolderExt } from '@papan/types'

export interface NameParts {
  base: string
  ext: string
  ext2: string
}

export function nameParts({ name, isFolder, fileType, isProject = isFolder && isProjectFolder(name) }: {
  name: string
  isFolder: boolean
  fileType?: string | null
  /** Whether the row is a project (the drive knows it from the row's type; defaults to the folder's name). */
  isProject?: boolean
}): NameParts {
  if (isPmetaFile(name) || pointerSuffixIsMeta(name, fileType)) {
    const dot = name.lastIndexOf('.')
    const stem = name.slice(0, dot)
    const ext2 = name.slice(dot) // `.pmeta` / `.shared` / `.chain` (lighter)
    const d = stem.lastIndexOf('.')
    return d > 0 ? { base: stem.slice(0, d), ext: stem.slice(d), ext2 } : { base: stem, ext: '', ext2 }
  }
  if (isProject) {
    const base = stripProjectFolderExt(name)
    return { base, ext: name.slice(base.length), ext2: '' }
  }
  if (isFolder && isMiniappFolder(name)) {
    const base = stripMiniappFolderExt(name)
    return { base, ext: name.slice(base.length), ext2: '' }
  }
  if (isFolder) return { base: name, ext: '', ext2: '' }
  const d = name.lastIndexOf('.')
  const base = d > 0 ? name.slice(0, d) : name
  return { base, ext: name.slice(base.length), ext2: '' }
}

/** The parts rendered: the base plain, each ending at its mute. */
export function MutedEndings({ parts }: { parts: NameParts }) {
  return (
    <>
      {parts.base}
      {parts.ext && <span className="file-ext-muted">{parts.ext}</span>}
      {parts.ext2 && <span className="file-ext-muted-2">{parts.ext2}</span>}
    </>
  )
}
